import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import PaytmPaymentStatus from "./PaytmPaymentStatus";
import { connect } from "react-redux";
import { useHistory } from "react-router";
import { url_19, url_8, url_300, url_303 } from "../api_services";
import uiActions from "../../redux/ui/action";
import authActions from "../../redux/auth/action";
import {
  clearAwaitPaymentFromUrl,
  clearPendingOrderId,
  getPendingOrderId,
  isPaymentCompleted,
  isPaymentFailed,
  isPaymentPending,
  PAYTM_CHECKOUT_FRAME,
  PAYTM_MAX_POLL_MS,
  PAYTM_POLL_INTERVAL_MS,
  PAYTM_POST_RETURN_MAX_RETRIES,
  PAYTM_POST_RETURN_RETRY_DELAY_MS,
  shouldFinalizePayment,
  submitPaytmCheckoutInFrame,
} from "./paytmHelpers";

import { Analytics } from "aws-amplify";

const PaytmResponse = (props) => {
  const {
    orderId,
    userData,
    token,
    tokenType,
    transactionData,
    paytmDetails,
    startLoading,
    finishLoading,
    setUser,
  } = props;

  const {
    course_id,
    batch_id,
    package_id,
    txn_amount,
    txn_discount_amount,
    txn_date,
    package_duration,
    txnid,
  } = transactionData;

  const [paytmResponse, setPaytmResponse] = useState({});
  const [resolvedOrderId, setResolvedOrderId] = useState("");
  const [isReceiptLoading, setIsReceiptLoading] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [isPendingVerification, setIsPendingVerification] = useState(false);
  const history = useHistory();
  const pollCleanupRef = useRef(null);
  const checkoutSubmittedRef = useRef(false);
  const iframeLoadCountRef = useRef(0);
  const paymentSettledRef = useRef(false);

  const getOrderIdFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("order_id") ||
      params.get("ORDERID") ||
      params.get("orderId") ||
      getPendingOrderId() ||
      ""
    );
  };

  const isAwaitingPayment = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get("await_payment") === "1";
  };

  const recordAnalytics = async (eventName) => {
    await Analytics.record({
      name: eventName,
      attributes: {
        course_id: course_id,
        batch_id: batch_id,
        itemprice: txn_amount,
        product_id: package_id,
        transactionDate: txn_date,
        user_name: userData.name,
        mobile: userData.mobile,
        userId: userData.user_id,
      },
    });
  };

  const showFailureResult = async () => {
    if (paymentSettledRef.current) return;
    paymentSettledRef.current = true;
    stopPolling();
    setIsPendingVerification(false);
    setShowCheckout(false);
    setShowResult(true);

    const currentOrderId = orderId || getOrderIdFromUrl();
    let failureData = { paymentStatus: "Failed" };

    if (currentOrderId) {
      try {
        const responseData = await fetchTransactionStatus(currentOrderId);
        if (responseData.status === 200) {
          failureData = responseData;
        }
      } catch (error) {
        console.log("Error fetching failure transaction status:", error);
      }
    }

    setPaytmResponse(failureData);
    clearAwaitPaymentFromUrl(history);
    await recordAnalytics("web_purchased_failed");
    clearPendingOrderId();
  };

  const showPendingVerificationResult = async (lastResponse = {}) => {
    if (paymentSettledRef.current) return;
    paymentSettledRef.current = true;
    stopPolling();
    setShowCheckout(false);
    setIsPolling(false);
    setShowResult(true);
    setIsPendingVerification(true);

    const currentOrderId = orderId || getOrderIdFromUrl();
    let pendingData = {
      paymentStatus: "Pending",
      receipt: {
        heading: "Pending Verification",
        sub_message:
          "Your payment is still being verified. This can take a few minutes.",
      },
      ...lastResponse,
    };

    if (currentOrderId && !lastResponse?.orderStatus) {
      try {
        const responseData = await fetchTransactionStatus(currentOrderId);
        if (responseData.status === 200) {
          pendingData = responseData;
        }
      } catch (error) {
        console.log("Error fetching pending transaction status:", error);
      }
    }

    setPaytmResponse(pendingData);
    clearAwaitPaymentFromUrl(history);
  };

  const handlePaymentResult = async (responseData) => {
    if (paymentSettledRef.current) return;
    paymentSettledRef.current = true;
    stopPolling();
    setIsPendingVerification(false);
    setShowCheckout(false);
    setShowResult(true);
    setPaytmResponse(responseData);
    clearAwaitPaymentFromUrl(history);
    clearPendingOrderId();

    if (isPaymentCompleted(responseData)) {
      saveUserTransactions();
      await recordAnalytics("web_purchased_success");
    } else {
      await recordAnalytics("web_purchased_failed");
    }
  };

  const fetchTransactionStatus = async (currentOrderId) => {
    const headers = {
      Authorization: `${tokenType} ${token}`,
    };

    const response = await axios.get(
      `${url_300}?order_id=${encodeURIComponent(currentOrderId)}`,
      { headers }
    );

    return response.data;
  };

  const getPaytmResponse = async () => {
    const currentOrderId = orderId || getOrderIdFromUrl();
    if (!currentOrderId) return;
    setResolvedOrderId(currentOrderId);

    try {
      const responseData = await fetchTransactionStatus(currentOrderId);
      if (
        responseData.status === 200 &&
        shouldFinalizePayment(responseData)
      ) {
        await handlePaymentResult(responseData);
      } else {
        await showFailureResult();
      }
    } catch (error) {
      console.log("Error checking Paytm transaction status:", error);
      await showFailureResult();
    }
  };

  const stopPolling = () => {
    if (pollCleanupRef.current) {
      pollCleanupRef.current();
      pollCleanupRef.current = null;
    }
  };

  const startPaymentPolling = (currentOrderId) => {
    let elapsed = 0;
    let settled = false;

    const cleanup = () => {
      clearInterval(pollTimer);
      setIsPolling(false);
    };

    const settlePayment = async (responseData) => {
      if (settled) return;
      settled = true;
      cleanup();
      await handlePaymentResult(responseData);
    };

    const pollStatus = async () => {
      try {
        const responseData = await fetchTransactionStatus(currentOrderId);

        if (
          responseData.status === 200 &&
          shouldFinalizePayment(responseData)
        ) {
          await settlePayment(responseData);
          return true;
        }
      } catch (error) {
        console.log("Error polling Paytm transaction status:", error);
      }
      return false;
    };

    pollStatus();

    const pollTimer = setInterval(async () => {
      elapsed += PAYTM_POLL_INTERVAL_MS;

      const finalized = await pollStatus();
      if (finalized) return;

      if (elapsed >= PAYTM_MAX_POLL_MS) {
        settled = true;
        cleanup();
        await showPendingVerificationResult();
      }
    }, PAYTM_POLL_INTERVAL_MS);

    pollCleanupRef.current = cleanup;
  };

  const finalizeAfterPaytmReturn = async () => {
    if (paymentSettledRef.current) return;

    stopPolling();
    setShowCheckout(false);
    setIsPolling(true);

    const currentOrderId = orderId || getOrderIdFromUrl();
    if (!currentOrderId) {
      await showFailureResult();
      return;
    }

    let lastResponse = null;

    for (let attempt = 0; attempt < PAYTM_POST_RETURN_MAX_RETRIES; attempt += 1) {
      try {
        const responseData = await fetchTransactionStatus(currentOrderId);
        if (responseData.status === 200) {
          lastResponse = responseData;

          if (isPaymentCompleted(responseData) || isPaymentFailed(responseData)) {
            setIsPolling(false);
            await handlePaymentResult(responseData);
            return;
          }
        }
      } catch (error) {
        console.log("Error checking status after Paytm redirect:", error);
      }

      if (attempt < PAYTM_POST_RETURN_MAX_RETRIES - 1) {
        await new Promise((resolve) =>
          setTimeout(resolve, PAYTM_POST_RETURN_RETRY_DELAY_MS)
        );
      }
    }

    setIsPolling(false);

    if (lastResponse && isPaymentPending(lastResponse)) {
      await showPendingVerificationResult(lastResponse);
      return;
    }

    if (lastResponse && isPaymentFailed(lastResponse)) {
      await handlePaymentResult(lastResponse);
      return;
    }

    await showFailureResult();
  };

  const handleCheckStatusAgain = async () => {
    const currentOrderId = resolvedOrderId || orderId || getOrderIdFromUrl();
    if (!currentOrderId) return;

    setIsPolling(true);
    try {
      const responseData = await fetchTransactionStatus(currentOrderId);

      if (responseData.status !== 200) return;

      if (isPaymentCompleted(responseData) || isPaymentFailed(responseData)) {
        paymentSettledRef.current = false;
        setIsPendingVerification(false);
        await handlePaymentResult(responseData);
        return;
      }

      setPaytmResponse(responseData);
      setIsPendingVerification(true);
      setShowResult(true);
    } catch (error) {
      console.log("Error checking payment status again:", error);
    } finally {
      setIsPolling(false);
    }
  };

  const handleIframeLoad = async () => {
    iframeLoadCountRef.current += 1;
    if (iframeLoadCountRef.current === 1) return;
    stopPolling();
    await finalizeAfterPaytmReturn();
  };

  useEffect(() => {
    const currentOrderId = orderId || getOrderIdFromUrl();
    if (!currentOrderId) return;
    setResolvedOrderId(currentOrderId);

    if (isAwaitingPayment() && Object.keys(paytmDetails || {}).length) {
      setShowCheckout(true);
      setIsPolling(true);
      startPaymentPolling(currentOrderId);
    } else {
      getPaytmResponse();
    }

    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showCheckout || checkoutSubmittedRef.current) return;
    if (!Object.keys(paytmDetails || {}).length) return;

    const timer = setTimeout(() => {
      const submitted = submitPaytmCheckoutInFrame(paytmDetails);
      checkoutSubmittedRef.current = submitted;
      if (!submitted) {
        showFailureResult();
      }
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCheckout, paytmDetails]);

  // When user clicks browser back arrow, go to dashboard only after showing result.
  const showResultRef = useRef(false);
  useEffect(() => {
    showResultRef.current = Boolean(showResult);
  }, [showResult]);

  useEffect(() => {
    const onPopState = () => {
      if (!showResultRef.current) return;
      history.replace("/user_dashboard");
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveUserTransactions = async () => {
    const headers = {
      Authorization: `${tokenType} ${token}`,
    };
    let dataDetails = {
      course_id,
      batch_id,
      package_id,
      txn_amount,
      txn_discount_amount,
      txn_date,
      package_duration,
      txnid,
    };

    startLoading();
    try {
      const response = await axios.post(url_19, dataDetails, { headers });
      if (response.data.status === 200) {
        finishLoading();
        await updateUser();
      }
    } catch (error) {
      console.log(error);
      finishLoading();
    }
  };

  const updateUser = async () => {
    startLoading();
    try {
      const response = await axios.get(url_8);
      if (response.data.status === 200) {
        setUser(response.data.data);
        finishLoading();
        return 1;
      }
      return 0;
    } catch (error) {
      console.log(error);
      return 0;
    }
  };

  const triggerPdfDownload = (blob, fileName) => {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const downloadReceipt = async () => {
    const currentOrderId =
      resolvedOrderId || orderId || getOrderIdFromUrl();
    if (!currentOrderId) {
      alert("Order id not found.");
      return;
    }

    setIsReceiptLoading(true);
    try {
      const response = await axios.get(url_303, {
        params: { order_id: currentOrderId },
        headers: {
          Authorization: `${tokenType} ${token}`,
        },
        responseType: "blob",
      });

      const contentType = response.headers?.["content-type"] || "";

      if (contentType.includes("application/pdf")) {
        triggerPdfDownload(
          new Blob([response.data], { type: "application/pdf" }),
          `receipt-${currentOrderId}.pdf`
        );
        return;
      }

      const responseText = await response.data.text();
      let payload = {};
      try {
        payload = JSON.parse(responseText);
      } catch (parseError) {
        console.log("Error parsing receipt response:", parseError);
      }

      const msg = payload?.message;
      alert(
        typeof msg === "string" && msg
          ? msg
          : "Could not download receipt."
      );
    } catch (error) {
      console.log("Error downloading receipt:", error);
      const errorPayload = error?.response?.data;
      let message = "";

      if (errorPayload instanceof Blob) {
        try {
          const parsed = JSON.parse(await errorPayload.text());
          message = parsed?.message || "";
        } catch (parseError) {
          console.log("Error parsing receipt error response:", parseError);
        }
      } else {
        message = errorPayload?.message || error?.message || "";
      }

      alert(message || "Could not download receipt. Please try again.");
    } finally {
      setIsReceiptLoading(false);
    }
  };

  const renderResult = () => (
    <PaytmPaymentStatus
      paytmResponse={paytmResponse}
      transactionData={transactionData}
      onDownloadReceipt={downloadReceipt}
      isReceiptLoading={isReceiptLoading}
      isPendingVerification={isPendingVerification}
      onCheckStatusAgain={handleCheckStatusAgain}
      isCheckingStatus={isPolling}
    />
  );

  if (showCheckout) {
    return (
      <iframe
        name={PAYTM_CHECKOUT_FRAME}
        title="Paytm Secure Payment"
        onLoad={handleIframeLoad}
        style={{
          height: "100vh",
          width: "100%",
          border: "none",
        }}
      />
    );
  }

  if (showResult && (Object.keys(paytmResponse).length || isPendingVerification)) {
    return renderResult();
  }

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#f1f5f9",
      }}
    >
      {!showResult && !showCheckout && isPolling ? (
        <div
          style={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "16px",
            color: "#263238",
          }}
        >
          Checking payment status...
        </div>
      ) : null}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    orderId: state.paymentReducer.orderId,
    userData: state.auth.user,
    token: state.auth.authToken,
    tokenType: state.auth.authType,
    transactionData: state.paymentReducer.transactionDetails,
    paytmDetails: state.paymentReducer.paytmDetails,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    startLoading: () => {
      dispatch(uiActions.startLoading());
    },
    finishLoading: () => {
      dispatch(uiActions.finishLoading());
    },
    setUser: (payload) => {
      dispatch(authActions.setUser(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PaytmResponse);
